import { useMemo } from "react";
import { IVideo } from "../hooks/useFetchVideos";
import { useUser } from "../../../../hooks/useUser";
import { isInternal } from "../../../../utils";
import { getBrandAllowedFileTypes } from "./helpers";

/**
 * The download permission gate, lifted verbatim out of `DownloadBtn` so the
 * panel-level "download all" actions cannot drift from the button beside them.
 *
 * Extracted rather than duplicated on purpose: this decides whether an
 * unapproved deliverable can leave the building, and two copies of that rule
 * is how they end up disagreeing. `DownloadBtn` now consumes this hook, so
 * every download surface on the page answers to one implementation.
 */

export interface DownloadGate {
  isInternalUser: boolean;
  /** Brand user whose brand has `pre_approval_downloading`. */
  canDownloadUnapproved: boolean;
  /** Anything at all may be downloaded. */
  canDownload: boolean;
  /** Unapproved + not a pre-approval downloader: watermarked is the ONLY variant. */
  showOnlyWatermarked: boolean;
  /** Brand's `enabled_file_types` allowlist, or null for "no restriction". */
  allowedTypes: Set<string> | null;
}

export interface DownloadGateOptions {
  /** Caller-resolved brand allowlist (public pages carry the brand on the order); `undefined` = derive. */
  allowedFileTypes?: Set<string> | null;
  /** Review surfaces: the viewer is the reviewer of exactly this unapproved version. */
  allowUnapproved?: boolean;
}

export const useDownloadGate = (
  video: IVideo,
  project?: any,
  ignoreFileTypeSettings = false,
  { allowedFileTypes, allowUnapproved = false }: DownloadGateOptions = {},
): DownloadGate => {
  const { role, user } = useUser();
  const isInternalUser = isInternal(role);

  const canDownloadUnapproved = useMemo(() => {
    if (isInternalUser) return true;

    const brandsToCheck: any[] = [];
    if (Array.isArray(video.brandOwners) && video.brandOwners.length > 0) {
      brandsToCheck.push(...video.brandOwners);
    }
    if (Array.isArray(project?.brands) && project.brands.length > 0) {
      brandsToCheck.push(...project.brands);
    }
    return brandsToCheck.some((brand: any) => brand && brand.pre_approval_downloading === true);
  }, [isInternalUser, role, user, video.brandOwners, project?.brands]);

  const allowedTypes = useMemo(() => {
    if (isInternalUser || ignoreFileTypeSettings) return null;
    if (allowedFileTypes !== undefined) return allowedFileTypes;
    return getBrandAllowedFileTypes([
      ...(Array.isArray(video.brandOwners) ? video.brandOwners : []),
      ...(Array.isArray(project?.brands) ? (project?.brands as any[]) : []),
      ...(video.brand ? [video.brand] : []),
    ]);
  }, [
    isInternalUser,
    ignoreFileTypeSettings,
    allowedFileTypes,
    video.brandOwners,
    project?.brands,
    video.brand,
  ]);

  const canDownload =
    isInternalUser ||
    canDownloadUnapproved ||
    project?.download_page ||
    allowUnapproved ||
    (typeof video.final === "boolean" ? video.final : true);

  // Non-internal only. The previous expression was inverted: `canDownloadUnapproved`
  // short-circuits to true for staff, so `(!isInternalUser || canDownloadUnapproved)`
  // could never be false for them, and an ADMIN opening any unapproved deliverable
  // got a download menu holding nothing but Watermarked. Staff take unapproved work
  // constantly — that is exactly what `canDownloadUnapproved` returning true means.
  //
  // NOTE: `ALWAYS_ON_FILE_TYPES` in helpers.ts documents a STRICTER rule than this —
  // "Watermarked is the only variant served for unapproved videos" — which would also
  // cover a brand with `pre_approval_downloading`. Tightening what a brand may take is
  // a permission change rather than a redesign, so today's behaviour is preserved.
  const showOnlyWatermarked =
    !isInternalUser &&
    !canDownloadUnapproved &&
    typeof video.final === "boolean" &&
    !video.final;

  return {
    isInternalUser,
    canDownloadUnapproved,
    canDownload,
    showOnlyWatermarked,
    allowedTypes,
  };
};
